import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../../utils/axiosConfig";
import { useParams } from "react-router-dom";

const EditPreferences = ({ preferences, onChange, onSubmit }) => {
  const { userId } = useParams();
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [localPreferences, setLocalPreferences] = useState({
    teethNotation: "Universal",
    language: "English (US)",
    disableRxTyping: false,
    technicianStamp: ""
  });

  const languages = [
    "English (US)", "Spanish", "French", "German", "Chinese (Simplified)",
    "Arabic", "Hindi", "Russian", "Portuguese", "Japanese", "Bengali",
    "Urdu", "Indonesian", "Turkish", "Italian", "Thai", "Korean",
    "Vietnamese", "Tamil", "Filipino",
  ];

  useEffect(() => {
    if (preferences) {
      setLocalPreferences({
        teethNotation: preferences.teethNotation || "Universal",
        language: preferences.language || "English (US)",
        disableRxTyping: preferences.disableRxTyping || false,
        technicianStamp: preferences.technicianStamp || ""
      });

      if (preferences.technicianStamp) {
        const stampPath = preferences.technicianStamp.startsWith('/')
          ? preferences.technicianStamp
          : `/${preferences.technicianStamp}`;
        setPreviewUrl(`https://hms.backend.hiragroupcloud.com${stampPath}`);
      } else {
        setPreviewUrl("");
      }
    }
  }, [preferences]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updatedValue = type === "checkbox" ? checked : value;

    const updatedPreferences = {
      ...localPreferences,
      [name]: updatedValue
    };

    setLocalPreferences(updatedPreferences);
    onChange(updatedPreferences);
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setFile(selectedFile);
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      setLocalPreferences(prev => ({
        ...prev,
        technicianStamp: selectedFile.name
      }));
    } else {
      setPreviewUrl(
        localPreferences?.technicianStamp
          ? `https://hms.backend.hiragroupcloud.com${localPreferences.technicianStamp}`
          : ""
      );
      setLocalPreferences(prev => ({
        ...prev,
        technicianStamp: preferences?.technicianStamp || ""
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (uploading) {
      toast.warning("Please wait, upload in progress...");
      return;
    }

    try {
      const toastId = toast.loading("Updating preferences...", {
        position: "top-center"
      });

      let technicianStampPath = localPreferences.technicianStamp;
      
      // Upload file if a new file is selected
      if (file) {
        setUploading(true);
        const formData = new FormData();
        formData.append("userId", userId);
        formData.append("technicianStamp", file);

        try {
          const uploadResponse = await api.post("/auth/upload-stamp", formData, {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          });

          if (uploadResponse.data.success) {
            technicianStampPath = uploadResponse.data.filePath;
            toast.update(toastId, {
              render: "Technician stamp uploaded successfully!",
              type: "success",
              isLoading: false,
              autoClose: 2000
            });
          } else {
            throw new Error(uploadResponse.data.message || "Failed to upload technician stamp");
          }
        } catch (uploadError) {
          console.error("Upload error:", uploadError);
          toast.update(toastId, {
            render: uploadError.response?.data?.message || uploadError.message || "Failed to upload stamp",
            type: "error",
            isLoading: false,
            autoClose: 3000
          });
          setUploading(false);
          return;
        } finally {
          setUploading(false);
        }
      }

      // Update preferences with the stamp path
      const updatedPreferences = {
        ...localPreferences,
        technicianStamp: technicianStampPath
      };

      // Call the onSubmit prop to save preferences
      const response = await onSubmit({ preferences: updatedPreferences });
      
      if (response) {
        toast.update(toastId, {
          render: "Preferences updated successfully!",
          type: "success",
          isLoading: false,
          autoClose: 2000
        });
        setFile(null); // Reset file input after successful upload
        setLocalPreferences(updatedPreferences);
        onChange(updatedPreferences);
      } else {
        throw new Error("Failed to update preferences");
      }
    } catch (error) {
      console.error("Error updating preferences:", error);
      toast.error(error.response?.data?.message || error.message || "Failed to update preferences", {
        position: "top-center",
        autoClose: 3000
      });
    }
  };

  // Clean up object URLs
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <div style={{ width: "120%", maxWidth: "1800px", margin: "0 auto", padding: "0px" }}>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <div className="row">
        <div className="col-md-10">
          <h1>Preferences</h1>
          <form onSubmit={handleSubmit}>
            <div
              style={{
                border: "1px solid #f8f9fa",
                borderTop: "3px solid #004aad",
                backgroundColor: "#e9ecef",
                padding: "20px"
              }}
            >
              <div>
                <h4>Teeth Notation</h4>
                <label style={{ marginLeft: "12%", display: "block" }}>
                  <input
                    type="radio"
                    name="teethNotation"
                    value="Universal"
                    checked={localPreferences.teethNotation === "Universal"}
                    onChange={handleInputChange}
                  />{" "}
                  Universal
                </label>
                <label style={{ marginLeft: "12%", display: "block" }}>
                  <input
                    type="radio"
                    name="teethNotation"
                    value="FDI"
                    checked={localPreferences.teethNotation === "FDI"}
                    onChange={handleInputChange}
                  />{" "}
                  FDI
                </label>
              </div>

              <div style={{ marginTop: "20px" }}>
                <h4>Language</h4>
                <select
                  name="language"
                  value={localPreferences.language}
                  onChange={handleInputChange}
                  style={{ marginLeft: "12%", padding: "5px", width: "200px" }}
                >
                  {languages.map((lang, index) => (
                    <option key={index} value={lang}>
                      {lang}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginTop: "20px" }}>
                <h4>Technician's Stamp</h4>
                <p>
                  Upload your technician stamp (PNG, JPEG, GIF)
                  <br />
                  Your stamp will be resized to 120px x 110px.
                </p>
                <input
                  type="file"
                  onChange={handleFileChange}
                  style={{ marginLeft: "12%" }}
                  accept="image/png,image/jpeg,image/gif"
                  disabled={uploading}
                />
                <div style={{ marginTop: "10px", marginLeft: "12%" }}>
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Technician Stamp Preview"
                      style={{
                        width: "120px",
                        height: "110px",
                        objectFit: "contain",
                        border: "1px solid #ccc",
                        backgroundColor: "#fff"
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "";
                        setPreviewUrl("");
                      }}
                    />
                  ) : (
                    <div style={{
                      width: "120px",
                      height: "110px",
                      border: "1px dashed #ccc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#666",
                      backgroundColor: "#fff"
                    }}>
                      No Stamp Uploaded
                    </div>
                  )}
                </div>
                {(file || localPreferences.technicianStamp) && (
                  <p style={{ marginLeft: "12%", marginTop: "10px", fontSize: "12px", color: "#666" }}>
                    {file ? file.name : localPreferences.technicianStamp.split('/').pop()}
                  </p>
                )}
              </div>

              <div
                style={{
                  marginLeft: "12%",
                  marginTop: "20px",
                  fontWeight: "bold",
                }}
              >
                <input
                  type="checkbox"
                  name="disableRxTyping"
                  checked={localPreferences.disableRxTyping}
                  onChange={handleInputChange}
                />{" "}
                Disable Rx Typing Suggestions
              </div>
              <div style={{ textAlign: "right", marginTop: "20px" }}>
                <button 
                  type="submit" 
                  style={{ 
                    backgroundColor: "#004aad", 
                    color: "#fff", 
                    border: "none", 
                    padding: "8px 16px", 
                    borderRadius: "4px",
                    cursor: uploading ? "not-allowed" : "pointer",
                    opacity: uploading ? 0.6 : 1
                  }}
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "Save Preferences"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditPreferences;